import React, { useEffect, useRef, useState, useMemo } from "react";
import { ChevronRight, X } from "lucide-react";
import { Category } from "../CategoryMegaMenu";
import { CategoryMegaPanel } from "./CategoryMegaPanel";
import { CategorySearchInput } from "../CategorySearchInput";
import {
  limitCategoryTreeDepth,
  searchCategories,
} from "../../utils/categoryUtils";
import { CategoryDisplayIcon } from "../CategoryDisplayIcon";

export interface HomepageCategoryMegaMenuProps {
  categories: Category[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  hoveredCategoryId: string | null;
  onHoverCategory: (categoryId: string | null) => void;
  onNavigate: (path: string) => void;
  topOffsetPx?: number;
  leftOffsetPx?: number;
}

export const HomepageCategoryMegaMenu: React.FC<
  HomepageCategoryMegaMenuProps
> = ({
  categories,
  isOpen,
  onOpenChange,
  hoveredCategoryId,
  onHoverCategory,
  onNavigate,
  topOffsetPx = 100,
  leftOffsetPx = 0,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const isCategoryActive = (category: Category): boolean => {
    const raw = category as any;
    if (typeof raw?.isActive === "boolean") return raw.isActive;
    if (typeof raw?.active === "boolean") return raw.active;
    if (typeof raw?.status === "string") {
      return raw.status.toLowerCase() === "active";
    }
    return true;
  };

  const level1Categories = useMemo(() => {
    const filterTree = (nodes: Category[]): Category[] =>
      (nodes || []).reduce<Category[]>((acc, node) => {
        if (!isCategoryActive(node)) return acc;
        const children = Array.isArray(node.children)
          ? filterTree(node.children)
          : [];
        acc.push({ ...node, children });
        return acc;
      }, []);

    // Header/browse menu: Industry + Product only (no Sub product level).
    return filterTree(limitCategoryTreeDepth(categories, 2));
  }, [categories]);

  const filteredCategories = useMemo(() => {
    return searchCategories(level1Categories, searchQuery);
  }, [level1Categories, searchQuery]);

  const hoveredCategory = hoveredCategoryId
    ? level1Categories.find((cat) => cat.id === hoveredCategoryId)
    : null;

  const menuTop = Math.max(0, Number(topOffsetPx) || 0);
  const menuLeft = Math.max(0, Number(leftOffsetPx) || 0);
  const menuHeight = `calc((100vh - ${menuTop}px) * 0.85)`;

  // ✅ Auto-select first category on open
  useEffect(() => {
    if (isOpen && !hoveredCategoryId && level1Categories.length > 0) {
      onHoverCategory(level1Categories[0].id);
    }
  }, [isOpen, hoveredCategoryId, level1Categories, onHoverCategory]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (menuRef.current && menuRef.current.contains(target)) return;

      const clickY = (event as MouseEvent).clientY;
      if (clickY < 100) {
        const element = target as Element;
        const clickableElement = element.closest(
          'a, button, [role="button"], [role="link"], div[class*="cursor-pointer"]'
        );
        if (clickableElement) return;
      }

      onOpenChange(false);
      onHoverCategory(null);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onOpenChange, onHoverCategory]);

  // Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onOpenChange(false);
        onHoverCategory(null);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () =>
        document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onOpenChange, onHoverCategory]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isOpen]);

  // Reset search
  useEffect(() => {
    if (!isOpen) setSearchQuery("");
  }, [isOpen]);

  const handleClose = () => {
    onOpenChange(false);
    onHoverCategory(null);
  };

  const handleCategoryHover = (categoryId: string) => {
    onHoverCategory(categoryId);
    if (!isOpen) onOpenChange(true);
  };

  // Close the menu after the parent routes; without this the fixed overlay keeps painting
  // over the destination page and body scroll stays locked, so clicks look broken.
  const handleNavigate = (path: string) => {
    onNavigate(path);
    onOpenChange(false);
    onHoverCategory(null);
  };

  if (!isOpen && !hoveredCategoryId) return null;

  return (
    <>
      <style>{`
        .category-mega-menu-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
        .category-mega-menu-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .category-mega-menu-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .category-mega-menu-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
          border: 2px solid #f1f5f9;
        }
        .category-mega-menu-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>

      <div
        ref={menuRef}
        className="fixed right-0 bg-white border-b border-gray-200 shadow-lg z-50"
        style={{ top: `${menuTop}px`, left: `${menuLeft}px`, height: menuHeight }}
      >
        <div className="w-full flex h-full relative px-2 sm:px-4 md:px-6 lg:px-8 xl:px-10">

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-50 p-2 hover:bg-gray-100 rounded-full transition-colors bg-white shadow-sm"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>

          {/* Sidebar */}
          <div
            ref={sidebarRef}
            className="w-[240px] sm:w-[260px] md:w-[280px] lg:w-[300px] xl:w-[320px] flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col"
            style={{ height: menuHeight }}
          >
            <div className="p-4 pb-3 bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                All Categories
              </h3>

              <CategorySearchInput
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>

            <div className="flex-1 overflow-y-auto category-mega-menu-scrollbar">
              <div className="py-2">
                {filteredCategories.map((category) => {
                  const isHovered = hoveredCategoryId === category.id;

                  return (
                    <div
                      key={category.id}
                      onMouseEnter={() => handleCategoryHover(category.id)}
                      onClick={() => {
                        handleCategoryHover(category.id);
                        if (!isOpen) onOpenChange(true);
                      }}
                      className={`relative flex items-center gap-3 justify-between px-5 py-2.5 cursor-pointer transition-colors ${
                        isHovered
                          ? "bg-white text-[#F97316] font-medium"
                          : "text-gray-700 hover:bg-white hover:text-[#F97316]"
                      }`}
                    >
                      {isHovered && (
                        <span
                          aria-hidden
                          className="absolute left-0 top-0 h-full w-[3px] bg-[#F97316]"
                        />
                      )}
                      <CategoryDisplayIcon
                        category={category}
                        imgClassName="h-7 w-7 object-contain shrink-0"
                        iconClassName={`h-5 w-5 shrink-0 ${
                          isHovered ? "text-[#F97316]" : "text-gray-600"
                        }`}
                      />
                      <span className="text-sm flex-1 truncate">
                        {category.name}
                      </span>
                      <ChevronRight
                        className={`h-4 w-4 shrink-0 transition-opacity ${
                          isHovered ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Panel */}
          {hoveredCategory && (
            <div
              className="flex-1 bg-white category-mega-menu-scrollbar"
              style={{
                height: menuHeight,
                overflowY: "auto",
              }}
            >
              {/* ✅ Removed max-width restriction */}
              <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8">
                <CategoryMegaPanel
                  category={hoveredCategory}
                  onNavigate={handleNavigate}
                  searchQuery={searchQuery}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed right-0 bottom-0 bg-black/10 backdrop-blur-sm z-40"
          style={{ top: `${menuTop}px`, left: `${menuLeft}px` }}
          onClick={handleClose}
        />
      )}
    </>
  );
};